import React from 'react';
import { Sparkles, Sun, Leaf, Snowflake } from 'lucide-react';
import { localDateToISO } from '../../../utils/dateHelpers';
import { SegmentedTabs } from '../V2Components';

// Northern-hemisphere season anchors used both for the API payload and the
// human-readable preview shown to the user. Picked to match the
// astronomical equinox / solstice dates so "Due ~ Mar 20" reads correctly.
export const SEASON_ANCHORS = {
    spring: { month: 3, day: 20, label: 'Spring', icon: Sparkles },
    summer: { month: 6, day: 20, label: 'Summer', icon: Sun },
    fall: { month: 9, day: 22, label: 'Fall', icon: Leaf },
    winter: { month: 12, day: 21, label: 'Winter', icon: Snowflake },
};

export const seasonAnchorISO = (season, year) => {
    const a = SEASON_ANCHORS[season];
    if (!a) return null;
    const mm = String(a.month).padStart(2, '0');
    const dd = String(a.day).padStart(2, '0');
    return localDateToISO(`${year}-${mm}-${dd}`);
};

/**
 * Client-side PREVIEW of the backend Season resolver (ADR-0009): the next
 * occurrence of the season's anchor strictly after today. Display-only —
 * the payload sends the intent (season) and the backend derives the date.
 */
export const nextSeasonAnchorISO = (season) => {
    const a = SEASON_ANCHORS[season];
    if (!a) return null;
    const now = new Date();
    let year = now.getFullYear();
    if (new Date(year, a.month - 1, a.day) <= now) year += 1;
    return seasonAnchorISO(season, year);
};

// testId values are the E2E contract — e2e/pages/maintenance/CreateTaskPage.ts
// locates these by `frequency-mode-*` exactly. Never rename them.
const MODE_TABS = [
    { key: 'one-time', label: 'One-time', testId: 'frequency-mode-one-time' },
    { key: 'recurring', label: 'Recurring', testId: 'frequency-mode-recurring' },
    { key: 'seasonal', label: 'Seasonal', testId: 'frequency-mode-seasonal' },
];

// Quarterly is intentionally absent — it's expressible as "monthly, every 3"
// and the editor reverse-maps existing quarterly tasks into that shape so the
// behavior is preserved without adding an extra picker option.
export const RECURRING_INTERVALS = [
    { value: 'daily', label: 'Daily', suffix: 'days' },
    { value: 'weekly', label: 'Weekly', suffix: 'weeks' },
    { value: 'monthly', label: 'Monthly', suffix: 'months' },
    { value: 'yearly', label: 'Yearly', suffix: 'years' },
];

const inputClasses = "w-full rounded-xl py-4 px-6 bg-[var(--bg-card)] border border-[var(--border-visible)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--primary-bg)] theme-transition";

const labelClasses = "block text-sm font-semibold text-[var(--text-secondary)] mb-2";

const ScheduleSection = ({ formData, onChange }) => {
    const setField = (k, v) => onChange({ [k]: v });
    const intervalMeta = RECURRING_INTERVALS.find(i => i.value === formData.recurrence_interval) || RECURRING_INTERVALS[1];

    const previewDate = formData.mode === 'seasonal'
        ? nextSeasonAnchorISO(formData.season)
        : null;
    // Format the ISO instant directly: seasonAnchorISO encodes LOCAL midnight
    // of the anchor day, so toLocaleDateString on the instant renders the
    // intended calendar day in every timezone. (Truncating the ISO string's
    // UTC date shifted the preview a day early in positive-offset zones.)
    const previewLabel = previewDate
        ? new Date(previewDate)
              .toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
        : null;

    return (
        <div className="space-y-6">
            {/* Frequency mode switch. This picks one of three form values rather
                than swapping navigation panels, so it's a radiogroup, not a tablist.
                ariaLabelledBy points at the visible label so it isn't announced twice. */}
            <div>
                <span id="frequency-label" className={labelClasses}>Frequency</span>
                <SegmentedTabs
                    tabs={MODE_TABS}
                    activeTab={formData.mode}
                    onSelect={(key) => setField('mode', key)}
                    semantics="radiogroup"
                    ariaLabelledBy="frequency-label"
                />
            </div>

            {/* Mode-specific fields */}
            {formData.mode === 'one-time' && (
                <div>
                    <label htmlFor="task-due-date" className={labelClasses}>Due Date</label>
                    <input
                        id="task-due-date"
                        type="date"
                        value={formData.due_date || ''}
                        onChange={e => setField('due_date', e.target.value)}
                        className={inputClasses}
                    />
                </div>
            )}

            {formData.mode === 'recurring' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-6">
                    <div>
                        <label htmlFor="task-recurrence-interval" className={labelClasses}>Repeats</label>
                        <select
                            id="task-recurrence-interval"
                            value={formData.recurrence_interval}
                            onChange={e => setField('recurrence_interval', e.target.value)}
                            className={inputClasses}
                        >
                            {RECURRING_INTERVALS.map(i => (
                                <option key={i.value} value={i.value}>{i.label}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="task-recurrence-value" className={labelClasses}>Every</label>
                        <div className="flex items-center gap-3">
                            <input
                                id="task-recurrence-value"
                                type="number"
                                min="1"
                                value={formData.recurrence_value}
                                onChange={e => setField('recurrence_value', e.target.value)}
                                className={inputClasses}
                            />
                            <span className="text-[var(--text-secondary)] font-medium whitespace-nowrap">
                                {intervalMeta.suffix}
                            </span>
                        </div>
                    </div>
                    <div>
                        <label htmlFor="task-start-date" className={labelClasses}>Starts</label>
                        <input
                            id="task-start-date"
                            type="date"
                            value={formData.due_date || ''}
                            onChange={e => setField('due_date', e.target.value)}
                            className={inputClasses}
                        />
                    </div>
                </div>
            )}

            {formData.mode === 'seasonal' && (
                <div className="space-y-6">
                    <div>
                        <label className={labelClasses}>Season</label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {Object.entries(SEASON_ANCHORS).map(([key, meta]) => {
                                const Icon = meta.icon;
                                const active = formData.season === key;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        data-testid={`season-pill-${key}`}
                                        onClick={() => setField('season', key)}
                                        className={`flex flex-col items-center justify-center gap-2 px-4 py-5 rounded-xl border transition-all ${
                                            active
                                                ? 'border-[var(--primary)] bg-[var(--primary-bg)] text-[var(--primary)] shadow-sm'
                                                : 'border-[var(--border-visible)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:border-[var(--primary)]'
                                        }`}
                                    >
                                        <Icon size={22} />
                                        <span className="font-semibold text-sm">{meta.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {previewLabel && (
                        <p className="text-sm text-[var(--text-muted)]">
                            {/* The date is derived by the backend Season resolver;
                                this is the matching client-side preview. */}
                            Next due ~ <span className="font-semibold text-[var(--text-secondary)]">{previewLabel}</span>
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default ScheduleSection;
